import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bike,
  BookOpen,
  CircleHelp,
  ListTodo,
  Pizza,
  ShoppingBag,
  Store,
  UtensilsCrossed,
} from "lucide-react";
import Tooltip from "../../../components/Tooltip";
import { producaoService } from "../../../services/producaoService";
import { useFilaEstatisticas } from "../../../hooks/useFilaEstatisticas";

interface DashboardPrimeiraLinhaProps {
  dataInicio: string;
  dataFim: string;
  lojaId?: number | null;
}

function formatQty(n: number): string {
  return n.toLocaleString("pt-BR");
}

function MetricLabel({
  label,
  tooltip,
  icon,
}: {
  label: string;
  tooltip?: string;
  icon?: React.ReactNode;
}) {
  return (
    <p className="flex min-w-0 items-center gap-1 text-[11px] font-medium text-neutral-600 sm:text-xs">
      {icon}
      <span className="min-w-0 truncate">{label}</span>
      {tooltip ? (
        <Tooltip text={tooltip} wide position="top">
          <button
            type="button"
            className="inline-flex shrink-0 rounded-full text-neutral-400 hover:text-neutral-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
            aria-label={`Ajuda: ${label}`}
          >
            <CircleHelp className="h-3.5 w-3.5" aria-hidden />
          </button>
        </Tooltip>
      ) : null}
    </p>
  );
}

export const DashboardPrimeiraLinha: React.FC<DashboardPrimeiraLinhaProps> = ({
  dataInicio,
  dataFim,
  lojaId,
}) => {
  const filters = useMemo(
    () => ({
      dataInicio,
      dataFim,
      lojaId: lojaId || undefined,
    }),
    [dataInicio, dataFim, lojaId]
  );

  const { data: producaoResumo, isLoading: isLoadingResumo } = useQuery({
    queryKey: ["producao-resumo", filters],
    queryFn: () => producaoService.getResumo(filters),
  });

  const { data: filaStats } = useFilaEstatisticas(lojaId);

  const pendentesFila = useMemo(() => {
    if (!filaStats) return 0;
    return (
      filaStats.filaSalgada.pendentes +
      filaStats.filaDoce.pendentes +
      (filaStats.filaSalaoSalgada?.pendentes ?? 0) +
      (filaStats.filaSalaoDoce?.pendentes ?? 0)
    );
  }, [filaStats]);

  return (
    <section className="space-y-2" aria-labelledby="dashboard-info-uteis">
      <h2
        id="dashboard-info-uteis"
        className="text-lg font-semibold text-neutral-800"
      >
        Informações Úteis
      </h2>

      <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm sm:p-4 lg:p-6">
        <div className="mb-3 flex items-center gap-2 sm:gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-orange-50 text-orange-600 sm:h-11 sm:w-11 lg:h-12 lg:w-12 [&_svg]:h-6 [&_svg]:w-6 sm:[&_svg]:h-7 sm:[&_svg]:w-7 lg:[&_svg]:h-8 lg:[&_svg]:w-8">
            <Pizza aria-hidden />
          </div>
          <p className="text-xs font-medium text-neutral-600 sm:text-sm">
            Produção no período
          </p>
        </div>

        {isLoadingResumo ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-14 animate-pulse rounded-md bg-neutral-100"
                aria-hidden
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-5">
            <div className="min-w-0 rounded-md bg-neutral-50 px-3 py-2.5">
              <MetricLabel
                label="Qtd. Produções"
                tooltip="Total de pizzas finalizadas no período — Cardápio Web, Salão, Rodízio e demais produções, sem exceção."
              />
              <p className="mt-0.5 text-lg font-bold tabular-nums text-neutral-900 sm:text-xl">
                {formatQty(producaoResumo?.totalProducoesTodas ?? 0)}
              </p>
            </div>

            <div className="min-w-0 rounded-md bg-neutral-50 px-3 py-2.5">
              <MetricLabel
                label="Qtd. Cardápio Web"
                icon={
                  <BookOpen
                    className="h-3 w-3 shrink-0 opacity-70"
                    aria-hidden
                  />
                }
                tooltip="Pizzas do Cardápio Web (integração e produções do fluxo delivery), excluindo salão e rodízio. Delivery e Balcão mostram a quantidade de pedidos (não de pizzas): Delivery = pedidos com entrega; Balcão = pedidos de retirada."
              />
              <p className="mt-0.5 text-lg font-bold tabular-nums text-neutral-900 sm:text-xl">
                {formatQty(producaoResumo?.totalProducoesCardapioWeb ?? 0)}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-neutral-600 sm:text-xs">
                <span className="inline-flex items-center gap-1">
                  <Bike className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                  Delivery:{" "}
                  <span className="font-semibold tabular-nums text-neutral-900">
                    {formatQty(
                      producaoResumo?.totalPedidosCardapioWebDelivery ?? 0
                    )}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1">
                  <ShoppingBag
                    className="h-3 w-3 shrink-0 opacity-70"
                    aria-hidden
                  />
                  Balcão:{" "}
                  <span className="font-semibold tabular-nums text-neutral-900">
                    {formatQty(
                      producaoResumo?.totalPedidosCardapioWebBalcao ?? 0
                    )}
                  </span>
                </span>
              </div>
            </div>

            <div className="min-w-0 rounded-md bg-neutral-50 px-3 py-2.5">
              <MetricLabel
                label="Qtd. Salão"
                icon={
                  <Store className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                }
                tooltip="Pizzas do Foodfly atendidas no salão e venda balcão."
              />
              <p className="mt-0.5 text-lg font-bold tabular-nums text-neutral-900 sm:text-xl">
                {formatQty(producaoResumo?.totalProducoesSalao ?? 0)}
              </p>
            </div>

            <div className="min-w-0 rounded-md bg-neutral-50 px-3 py-2.5">
              <MetricLabel
                label="Qtd. Rodízio"
                icon={
                  <UtensilsCrossed
                    className="h-3 w-3 shrink-0 opacity-70"
                    aria-hidden
                  />
                }
                tooltip="Pizzas do rodízio finalizadas no período."
              />
              <p className="mt-0.5 text-lg font-bold tabular-nums text-neutral-900 sm:text-xl">
                {formatQty(producaoResumo?.totalProducoesRodizio ?? 0)}
              </p>
            </div>

            <div className="min-w-0 rounded-md bg-neutral-50 px-3 py-2.5">
              <MetricLabel
                label="Falta Montar"
                icon={
                  <ListTodo
                    className="h-3 w-3 shrink-0 opacity-70"
                    aria-hidden
                  />
                }
              />
              <p className="mt-0.5 text-lg font-bold tabular-nums text-neutral-900 sm:text-xl">
                {formatQty(pendentesFila)}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
